import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { fileUrl, slDateTime } from "@/lib/api";

export default function Lightbox({ items, index, onClose, onIndex, actions }) {
  const touch = useRef(null);
  const item = index != null ? items[index] : null;

  useEffect(() => {
    if (index == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndex(Math.min(items.length - 1, index + 1));
      if (e.key === "ArrowLeft") onIndex(Math.max(0, index - 1));
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [index, items.length, onClose, onIndex]);

  const onTouchEnd = (e) => {
    if (touch.current == null) return;
    const dx = e.changedTouches[0].clientX - touch.current;
    if (dx < -50 && index < items.length - 1) onIndex(index + 1);
    if (dx > 50 && index > 0) onIndex(index - 1);
    touch.current = null;
  };

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-[#0F0E0E]/95 backdrop-blur-xl text-white flex flex-col"
          data-testid="lightbox"
          onTouchStart={(e) => (touch.current = e.touches[0].clientX)} onTouchEnd={onTouchEnd}
        >
          <div className="flex items-center justify-between px-4 py-3 text-sm">
            <span className="text-white/70" data-testid="lightbox-counter">{index + 1} / {items.length}</span>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10" data-testid="lightbox-close" aria-label="Zapri"><X /></button>
          </div>
          <div className="flex-1 relative flex items-center justify-center px-2 min-h-0">
            <motion.div key={item.id} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25 }} className="w-full h-full flex items-center justify-center">
              {item.type === "video" ? (
                <video src={fileUrl(item.original_url)} poster={fileUrl(item.thumb_url) || undefined} controls autoPlay playsInline className="max-h-full max-w-full rounded-lg" data-testid="lightbox-video" />
              ) : (
                <img src={fileUrl(item.preview_url || item.original_url)} alt={`Fotografiral: ${item.uploader_name}`} className="max-h-full max-w-full object-contain rounded-lg select-none" data-testid="lightbox-image" draggable={false} />
              )}
            </motion.div>
            {index > 0 && (
              <button onClick={() => onIndex(index - 1)} className="hidden sm:grid absolute left-4 w-12 h-12 place-items-center rounded-full bg-white/10 hover:bg-white/20" data-testid="lightbox-prev" aria-label="Prejšnja"><ChevronLeft /></button>
            )}
            {index < items.length - 1 && (
              <button onClick={() => onIndex(index + 1)} className="hidden sm:grid absolute right-4 w-12 h-12 place-items-center rounded-full bg-white/10 hover:bg-white/20" data-testid="lightbox-next" aria-label="Naslednja"><ChevronRight /></button>
            )}
          </div>
          <div className="px-5 py-4 flex flex-wrap items-center justify-between gap-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <div>
              <p className="font-display text-lg" data-testid="lightbox-uploader">{item.type === "video" ? "Posnel" : "Fotografiral"}: {item.uploader_name}</p>
              <p className="text-xs text-white/50">{slDateTime(item.created_at)}</p>
            </div>
            <div className="flex items-center gap-2">{actions && actions(item)}</div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
